import React from 'react';
import { Card } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import './GenericPageSkeleton.css';

/**
 * Generic full-page skeleton used as Suspense fallback for lazy-loaded pages.
 * Uses react-loading-skeleton for consistent shimmer animation across the app.
 * Mirrors a typical page: title bar + main card with tabs + content rows.
 */
const GenericPageSkeleton: React.FC = () => (
  <div style={{ padding: '4px 0' }}>
    {/* Page title + action button */}
    <div className="d-flex justify-content-between align-items-center mb-4">
      <Skeleton width={200} height={28} borderRadius={6} />
      <Skeleton width={130} height={36} borderRadius={8} />
    </div>

    {/* Main content card */}
    <Card className="border-0 shadow-sm rounded-4 mb-4">
      <Card.Body className="p-4">
        {/* Tabs row */}
        <div className="d-flex gap-3 mb-4">
          <Skeleton width={90} height={16} borderRadius={6} />
          <Skeleton width={80} height={16} borderRadius={6} />
          <Skeleton width={100} height={16} borderRadius={6} />
        </div>
        <div style={{ borderTop: '1px solid #f0f0f0', marginBottom: 20 }} />

        {/* Content rows */}
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="d-flex align-items-center gap-3 py-3"
            style={{ borderBottom: '1px solid #f8f8f8' }}
          >
            <Skeleton circle width={40} height={40} style={{ flexShrink: 0 }} />
            <div className="flex-grow-1">
              <Skeleton width={`${55 + (i % 3) * 10}%`} height={14} borderRadius={6} style={{ marginBottom: 6 }} />
              <Skeleton width={`${35 + (i % 2) * 15}%`} height={12} borderRadius={6} />
            </div>
            <Skeleton width={80} height={30} borderRadius={8} style={{ flexShrink: 0 }} />
          </div>
        ))}
      </Card.Body>
    </Card>

    {/* Secondary card */}
    <Card className="border-0 shadow-sm rounded-4">
      <Card.Body className="p-4">
        <Skeleton width="40%" height={20} borderRadius={6} style={{ marginBottom: 16 }} />
        <div style={{ borderTop: '1px solid #f0f0f0', marginBottom: 16 }} />
        <div className="row g-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="col-12 col-md-4">
              <Skeleton width="100%" height={100} borderRadius={10} />
            </div>
          ))}
        </div>
      </Card.Body>
    </Card>
  </div>
);

export default GenericPageSkeleton;
